<template>
	<SuiModalDialog class="time-off-modify-form">
		<template #header>
			<div class="time-off-modify-form__header">
				<h4 class="time-off-modify-form__heading">{{ heading }}</h4>
				<template v-if="showUserNameInHeader">
					<SuiIcon name="icon-arrow-right-16" />
					<span class="time-off-modify-form__user-name">
						{{ currentUserName }}
					</span>
				</template>
			</div>
		</template>

		<div class="time-off-modify-form__content">
			<SuiSkeleton
				v-if="loadingTimeOffItem || isLoadingTimeOfTypes"
				height="3.75rem"
				width="100%"
			/>
			<SuiSelect
				v-else
				:model-value="timeOffData.type"
				:aria-label="$t('type')"
				hide-clear-button
				:options="typeOptions"
				class="time-off-modify-form__type-select"
				:label="$t('type')"
				@update:model-value="onUpdateType"
			/>
			<SuiInput
				v-if="showReasonField"
				v-model="timeOffData.reason"
				class="time-off-modify-form__type-input"
				:label="$t('time-off__reason')"
			/>
			<div class="time-off-modify-form__inputs-wrapper">
				<SuiSkeleton
					v-if="loadingTimeOffItem"
					height="3.75rem"
					width="28rem"
				/>
				<div v-else class="time-off-modify-form__date-select-wrapper">
					<AppDatePicker
						:model-value="timeOffData.startDate"
						data-testid="time-off-start-date-input"
						class="time-off-modify-form__start-date-input"
						:format="dateFormat"
						:label="$t('time-off__starting-from')"
						:variant="v.startDate.$error ? 'error' : undefined"
						:message="
							v.startDate.$error
								? v.startDate.$errors[0]?.$message.toString()
								: undefined
						"
						append-to-body
						hide-clear-button
						@update:model-value="onUpdateStartDate"
					/>
					<AppDatePicker
						ref="endDatePickerRef"
						:model-value="timeOffData.endDate"
						data-testid="time-off-end-date-input"
						class="time-off-modify-form__end-date-input"
						:date-disabled="isBeforeStartDate"
						:format="dateFormat"
						:label="$t('time-off__ending-on')"
						:variant="v.endDate.$error ? 'error' : undefined"
						:message="
							v.endDate.$error
								? v.endDate.$errors[0]?.$message.toString()
								: undefined
						"
						append-to-body
						hide-clear-button
						@update:model-value="onUpdateEndDate"
					/>
				</div>
				<SuiSkeleton
					v-if="loadingTimeOffItem"
					height="3.75rem"
					width="13.75rem"
				/>
				<SuiSelect
					v-else-if="showDurationSelect"
					ref="durationSelectRef"
					v-model="timeOffData.duration"
					class="time-off-modify-form__duration-select"
					:label="$t('time_off_duration')"
					:options="durationOptions"
					hide-clear-button
				/>
				<SuiTimeInput
					v-if="showTimeInput"
					v-model="timeOffData.amountTime"
					:label="timeInputLabel"
					class="time-off-modify-form__duration-input"
					:variant="v.amountTime.$error ? 'error' : undefined"
					:allow-empty="false"
					:hours-per-day="hoursPerDay"
					@blur="validateInputField('amountTime')"
					@clear="onClear"
				/>
			</div>
			<SuiSkeleton v-if="loadingTimeOffItem" height="4rem" width="100%" />
			<UserSelectList
				v-else-if="canChangeTimeOffForOthers"
				:model-value="timeOffData.userSelection"
				class="time-off-modify-form__user-select-input"
				divider-text=","
				:show-switch="false"
				:any-user-text="$t('select_list_users_groups_all_users')"
				:no-users-text="$t('time-off__users-not-selected')"
				:variant="v.userSelection.$error ? 'error' : undefined"
				show-selected-users-first
				@update:model-value="onUpdateUserSelection"
			/>
		</div>

		<template #footer>
			<AppConfirmationPopover
				v-if="isModifyForm"
				v-slot="{ openConfirmation }"
				class="time-off-modify-form__delete-button"
				message="time-off__reset-availability-confirmation-selected-users"
				:primary-button-label="
					$t(
						'time-off__reset-availability-selected-users-confirmation-label'
					)
				"
				:secondary-button-label="
					$t('time-off__reset-availability-cancel-label')
				"
				@confirm="onDelete(id)"
			>
				<SuiTextButton
					v-tooltip="$t('time-off__delete')"
					:disabled="loadingTimeOffItem"
					:aria-label="$t('time-off__delete')"
					@click="openConfirmation"
				>
					<SuiIcon name="icon-bin-16" />
				</SuiTextButton>
			</AppConfirmationPopover>

			<div class="time-off-modify-form__buttons-wrapper">
				<SuiButton variant="secondary-outlined" @click="onClose">
					{{ $t('cancel') }}
				</SuiButton>
				<SuiButton
					variant="primary"
					:is-loading="loadingSave"
					:disabled="v.$errors.length > 0 || loadingSave"
					@click="onSave"
				>
					{{ $t('save') }}
				</SuiButton>
			</div>
		</template>
	</SuiModalDialog>
</template>

<script setup lang="ts">
import {
	SuiButton,
	SuiIcon,
	SuiInput,
	SuiModalDialog,
	SuiSelect,
	SuiSkeleton,
	SuiTextButton,
	SuiTimeInput,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import { ref, computed, toRefs, reactive, onMounted } from 'vue';
import {
	useElementBackgroundClick,
	useFormatter,
	usePermissions,
	useSettings,
	useTranslation,
} from '@/composables';
import {
	type User,
	type TimeOff,
	useTimeOffDeleteMutation,
	useTimeOffModifyFormQuery,
	useTimeOffModifyFormSaveMutation,
	useTimeOffTypeQuery,
} from '@/generated/graphql';
import { UserSelectList } from '@/features/User';
import { AppConfirmationPopover, AppDatePicker } from '@/features/App';
import { resetTime, shortDuration } from '@/utils/formatting';
import {
	ALL_DAY_VALUE,
	DEFAULT_TIME_AMOUNT,
	TimeOffDuration,
} from './constants';
import { type TimeOffData } from './types';
import { useVuelidate } from '@vuelidate/core';
import { largerThanZeroTime, validDate } from '@/utils/validators';
import { useCurrentUser } from '@/features/Auth';
import { useMessage } from '@/features/Message';
import { wait } from '@/utils/misc';
import { timeToSeconds } from '@/utils/time';
import { PermissionAction, Permission } from '@/constants/permissions';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { isUserListNotEmpty, isWithinMaxSecondsPerDay } from './helpers';
import { AmplitudeCustomEvents, track } from '@/lib/amplitude';
import { TimeOffSystemType } from '@/features/Calendar/components/TimeOff';
import { Constants } from '@/constants';

export type TimeOffModifyFormProps = {
	isOpen: boolean;
	startDate?: string;
	endDate?: string;
	userIds?: Array<User['id']>;
	id?: TimeOff['id'];
};

const emit = defineEmits<{
	(e: 'close'): void;
	(e: 'save'): void;
}>();

const props = defineProps<TimeOffModifyFormProps>();

const { dateFormatForSUIdatePicker: dateFormat } = useFormatter();
const { t } = useTranslation();
const { currentUser } = useCurrentUser();
const { addSuccessMessage } = useMessage();
const { can } = usePermissions();
const { setting } = useSettings();

const { userIds, startDate, endDate, isOpen, id } = toRefs(props);

const isModifyForm = computed(() => Boolean(id.value));

const hoursPerDay = computed(() =>
	Number(setting(Constants.Setting.SYSTEM_DAY_UNIT_SUB_VALUE))
);

const {
	result: resultTimeOffItem,
	onResult: onResultTimeOffItem,
	loading: loadingTimeOffItem,
} = useTimeOffModifyFormQuery(
	{
		id: id.value || '',
	},
	{
		enabled: isModifyForm.value,
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	}
);

const { result: resultTimeOffTypes, loading: isLoadingTimeOfTypes } =
	useTimeOffTypeQuery({
		timeOffTypeInput: {
			isInUse: true,
		},
	});

const {
	mutate: saveTimeOff,
	loading: loadingSave,
	onDone: onDoneSave,
} = useTimeOffModifyFormSaveMutation({});

const { mutate: deleteTimeOff, onDone: onDoneDelete } =
	useTimeOffDeleteMutation({});

const durationSelectRef = ref<typeof SuiSelect | null>(null);
const endDatePickerRef = ref<typeof AppDatePicker | null>(null);
const selectedUserNames = ref<Array<User['name']>>([]);

const getTimeOffInitialValues = (): TimeOffData => ({
	userSelection: {
		itemIds: userIds.value || [],
		groupIds: [],
	},
	startDate: startDate.value || dateToMysqlString(new Date()),
	endDate: endDate.value || dateToMysqlString(new Date()),
	type: TimeOffSystemType.Vacation,
	duration: TimeOffDuration.AllDay,
	reason: '',
	amountTime: shortDuration(DEFAULT_TIME_AMOUNT)[0].value,
});

const timeOffData = reactive<TimeOffData>({ ...getTimeOffInitialValues() });

const heading = computed(() =>
	isModifyForm.value ? t('time-off__modify') : t('time_off')
);

const currentUserId = computed(() => currentUser.value?.id || '');
const currentUserName = computed(() => currentUser.value?.name);

const canChangeTimeOffForOthers = computed(() =>
	can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_CHANGE_TIMEOFF_FOR_OTHER_USERS
	)
);

const timeOffEntryUserIds = computed(
	() => resultTimeOffItem.value?.TimeOffItem.users.map(({ id }) => id) || []
);

const showUserNameInHeader = computed(
	() => !canChangeTimeOffForOthers.value && currentUserName.value
);

const timeInputLabel = computed(() =>
	timeOffData.type === TimeOffSystemType.ExtraAvailability
		? t('time-off__extra-daily-hours')
		: t('time_off_duration')
);

const showReasonField = computed(
	() =>
		timeOffData.type === TimeOffSystemType.Other ||
		timeOffData.type === TimeOffSystemType.ExtraAvailability
);

const showDurationSelect = computed(
	() =>
		timeOffData.duration === TimeOffDuration.AllDay &&
		timeOffData.type !== TimeOffSystemType.ExtraAvailability
);

const showTimeInput = computed(
	() =>
		timeOffData.duration === TimeOffDuration.ExactTime ||
		timeOffData.type === TimeOffSystemType.ExtraAvailability
);

const typeOptions = computed(
	() =>
		resultTimeOffTypes?.value?.TimeOffTypesList.map((item) => ({
			id: item.systemName,
			title: item.displayName,
		})) || []
);

const durationOptions = computed(() => [
	{
		id: TimeOffDuration.AllDay,
		title: t('time-off__all-day'),
	},
	{
		id: TimeOffDuration.ExactTime,
		title: t('time-off__exact-time'),
	},
]);

const v = useVuelidate(
	{
		amountTime: { largerThanZeroTime, isWithinMaxSecondsPerDay },
		userSelection: { isUserListNotEmpty },
		startDate: { validDate },
		endDate: { validDate },
	},
	timeOffData,
	{ $rewardEarly: true }
);

const validateInputField = (field: string) => {
	v.value[field].$touch();
	v.value[field].$commit();
};

const isBeforeStartDate = (date: string) =>
	resetTime(date) < resetTime(timeOffData.startDate);

const checkAndMoveEndDate = (endDateValue: Date, startDateValue: Date) => {
	if (resetTime(endDateValue) < resetTime(startDateValue)) {
		timeOffData.endDate = timeOffData.startDate;
	}
};

const onUpdateStartDate = async (selectedStartDate: string) => {
	const isStartDateChanged = selectedStartDate !== timeOffData.startDate;

	timeOffData.startDate = selectedStartDate;

	checkAndMoveEndDate(
		resetTime(timeOffData.endDate),
		resetTime(timeOffData.startDate)
	);

	validateInputField('startDate');

	if (isStartDateChanged && endDatePickerRef.value) {
		const endDateInput = endDatePickerRef.value.$el.querySelector('input');
		endDateInput.click();
		endDateInput.focus();
	}
};

const onUpdateEndDate = (selectedEndDate: string) => {
	timeOffData.endDate = selectedEndDate;

	validateInputField('endDate');

	checkAndMoveEndDate(
		resetTime(timeOffData.endDate),
		resetTime(timeOffData.startDate)
	);
};

const onUpdateType = (typeOffType: string) => {
	timeOffData.reason = '';
	timeOffData.type = typeOffType;
};

const onClear = async () => {
	timeOffData.duration = TimeOffDuration.AllDay;
	timeOffData.amountTime = shortDuration(DEFAULT_TIME_AMOUNT)[0].value;

	await wait(100);

	if (durationSelectRef.value) {
		durationSelectRef.value.$el.querySelector('button').click();
	}
};

const resetTimeOffData = () => {
	Object.assign(timeOffData, { ...getTimeOffInitialValues() });
};

const onClose = async () => {
	emit('close');

	if (!isModifyForm.value) {
		await wait(100);
		resetTimeOffData();
	}
};

const getDurationValue = () => {
	if (
		timeOffData.type !== TimeOffSystemType.ExtraAvailability &&
		timeOffData.duration === TimeOffDuration.AllDay
	) {
		return ALL_DAY_VALUE;
	}

	return timeToSeconds(timeOffData.amountTime);
};

const showSuccessMessage = (userNames: Array<User['name']>) => {
	const sortedUsersNames = [...userNames].sort((a, b) => b.localeCompare(a));

	const userNameOne = sortedUsersNames[0];
	const userNameTwo = sortedUsersNames[1];

	if (sortedUsersNames.length === 1) {
		addSuccessMessage({
			message: t('time-off__save-success-one-user', {
				userName: userNameOne,
			}),
		});
	}

	if (sortedUsersNames.length === 2) {
		addSuccessMessage({
			message: t('time-off__save-success-two-users', {
				userNameOne,
				userNameTwo,
			}),
		});
	}

	if (sortedUsersNames.length > 2) {
		addSuccessMessage({
			message: t('time-off__save-success-multiple-users', {
				userNameOne,
				userNameTwo,
				hiddenUserNameCount: sortedUsersNames.length - 2,
			}),
		});
	}
};

const onSave = async () => {
	const userIds = canChangeTimeOffForOthers.value
		? timeOffData.userSelection.itemIds
		: [currentUserId.value];

	await saveTimeOff({
		input: {
			endDate: timeOffData.endDate,
			id: id.value ?? undefined,
			reason: timeOffData.reason.trim(),
			startDate: timeOffData.startDate,
			type: timeOffData.type,
			userIds,
			value: getDurationValue(),
		},
	});
};

const onDelete = async (id?: TimeOff['id']) => {
	if (id) {
		await deleteTimeOff({
			input: {
				id,
				userIds: timeOffEntryUserIds.value,
			},
		});
	}
};

onDoneSave((response) => {
	if (response?.data) {
		const { users, timeOff } = response.data.TimeOffSave;

		const userNames = users.map(({ name }) => name);

		showSuccessMessage(userNames);

		track(AmplitudeCustomEvents.TimeOffCreated, {
			action_type: isModifyForm.value ? 'modified' : 'created',
			time_off_type: timeOff.type.systemName?.toLowerCase(),
			duration_type:
				timeOff.value === ALL_DAY_VALUE ? 'all day' : 'hours',
		});
	}

	onClose();
	emit('save');
});

onDoneDelete(() => {
	showSuccessMessage(selectedUserNames.value);

	onClose();
	emit('save');
});

onResultTimeOffItem((result) => {
	if (result?.data) {
		const { timeOff, users } = result.data.TimeOffItem;
		const userNames = users.map(({ name }) => name);
		const newItemIds = users.map(({ id }) => id);

		selectedUserNames.value = userNames;
		timeOffData.userSelection.itemIds = newItemIds;
		timeOffData.type = timeOff.type.systemName;
		timeOffData.reason = timeOff.reason?.trim() || '';
		timeOffData.startDate = timeOff.startDate;
		timeOffData.endDate = timeOff.endDate;
		timeOffData.duration =
			timeOff.value === ALL_DAY_VALUE
				? TimeOffDuration.AllDay
				: TimeOffDuration.ExactTime;
		timeOffData.amountTime =
			timeOff.value === ALL_DAY_VALUE
				? shortDuration(DEFAULT_TIME_AMOUNT)[0].value
				: shortDuration(timeOff.value)[0].value;
	}
});

const onUpdateUserSelection = (
	selection: SuiSelectListItemsAndGroupsValueType
) => {
	timeOffData.userSelection = selection;

	validateInputField('userSelection');
};

useElementBackgroundClick('time-off-modify-form', isOpen, onClose);

const setPopperZIndexForTimeOffPopovers = () => {
	const popperElements = document.querySelectorAll('.v-popper__popper');

	popperElements.forEach((popperElement) => {
		const timeOffListPopoverElement = popperElement.querySelector(
			'.time-off-list-popover'
		);
		const timeOffListPopoverDropdownElement = popperElement.querySelector(
			'.time-off-list-popover-dropdown'
		);

		const isAnyChildTimeOffPopover =
			timeOffListPopoverElement || timeOffListPopoverDropdownElement;

		if (isAnyChildTimeOffPopover && popperElement instanceof HTMLElement) {
			popperElement.style.zIndex = '101';
		}
	});
};

onMounted(() => {
	setPopperZIndexForTimeOffPopovers();
});
</script>

<style lang="scss">
.time-off-modify-form {
	--sui-modal-dialog-max-width: 51.3rem;

	&__header {
		display: flex;
		align-items: center;
		align-self: stretch;
		gap: 0.5rem;
	}

	&__user-name {
		font-size: 1.625rem;
		color: $neutral-70;
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2rem;
	}

	&__type-select,
	&__type-input,
	&__user-select-input {
		width: 100%;
	}

	&__duration-select,
	&__duration-input,
	&__start-date-input,
	&__end-date-input {
		width: 14rem;
	}

	&__inputs-wrapper {
		display: flex;
		align-items: center;
		gap: 2rem;
	}

	&__date-select-wrapper {
		display: flex;
	}

	&__start-date-input {
		.sui-input {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}
	}

	&__end-date-input {
		.sui-input {
			border-left: 0;
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}

	&__user-select-input {
		.sui-horizontal-truncating-list__truncated-indicator {
			margin-left: 0.5rem;
		}
	}

	&__delete-button {
		--sui-color-link: #{$neutral-60};
		--sui-color-link-hover: #{$neutral-70};

		display: flex;
		margin-right: auto;
		padding: 1rem;
	}

	&__buttons-wrapper {
		display: flex;
		align-items: center;
		gap: 1rem;
	}
}
</style>
